import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";

const TYPE_BADGE = {
  advertisement: { label: "Ad", color: "#F59E0B" },
  promotion: { label: "Promo", color: "#10B981" },
  announcement: { label: "News", color: "#3B82F6" },
  discount: { label: "Discount", color: "#EC4899" },
  event: { label: "Event", color: "#FB923C" },
  partner: { label: "Partner", color: "#06B6D4" },
  feature: { label: "New", color: "#A78BFA" },
  offer: { label: "Offer", color: "#14B8A6" },
};

/** Premium auto-sliding banner carousel — swipe, arrows, dots. */
export const BannerCarousel = () => {
  const nav = useNavigate();
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);
  const touchX = useRef(null);
  const viewed = useRef(new Set());

  useEffect(() => {
    api.get("/banners").then((r) => setBanners(r.data.banners || [])).catch(() => {});
  }, []);

  const next = useCallback(() => setIdx((i) => (banners.length ? (i + 1) % banners.length : 0)), [banners.length]);
  const prev = () => setIdx((i) => (banners.length ? (i - 1 + banners.length) % banners.length : 0));

  useEffect(() => {
    if (banners.length < 2) return;
    timer.current = setInterval(next, 5000);
    return () => clearInterval(timer.current);
  }, [banners.length, next]);

  useEffect(() => {
    const b = banners[idx];
    if (b && !viewed.current.has(b.id)) {
      viewed.current.add(b.id);
      api.post(`/banners/${b.id}/event`, { type: "view" }).catch(() => {});
    }
  }, [idx, banners]);

  if (!banners.length) return null;
  const b = banners[idx];
  const badge = TYPE_BADGE[b.type] || TYPE_BADGE.promotion;

  const act = () => {
    api.post(`/banners/${b.id}/event`, { type: "click" }).catch(() => {});
    const dest = b.button_action || "";
    if (!dest) return;
    if (/^https?:\/\//i.test(dest)) window.open(dest, "_blank");
    else nav(dest);
  };

  return (
    <section
      className="relative rounded-2xl overflow-hidden select-none"
      data-testid="banner-carousel"
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (dx > 45) prev(); else if (dx < -45) next();
        touchX.current = null;
      }}
    >
      <div className="relative h-40 sm:h-48 bg-gradient-to-br from-[#1E1145] to-[#09090F]">
        {b.image_url && (
          <img key={b.id} src={b.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${b.image_url}` : b.image_url}
               alt="" className="icaard-banner-image absolute inset-0 w-full h-full object-cover opacity-60 transition-opacity duration-500" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/35 to-transparent" />
        <div key={`t-${b.id}`} className="absolute inset-0 p-5 sm:p-6 flex flex-col justify-center max-w-[75%] animate-[bannerIn_.45s_ease]">
          <span className="inline-flex w-fit items-center text-[9px] font-black uppercase tracking-[0.2em] px-2 py-0.5 rounded-full mb-2"
                style={{ background: `${badge.color}26`, color: badge.color, border: `1px solid ${badge.color}66` }}>
            {badge.label}
          </span>
          <h3 className="font-display font-black text-lg sm:text-2xl leading-tight text-white" data-testid="banner-title">{b.title}</h3>
          {b.description && <p className="text-[11px] sm:text-xs text-white/75 mt-1 line-clamp-2">{b.description}</p>}
          {b.button_text && (
            <button onClick={act} data-testid="banner-cta"
                    className="mt-3 w-fit px-4 py-1.5 rounded-full bg-white text-black text-xs font-bold hover:scale-105 transition-transform">
              {b.button_text}
            </button>
          )}
        </div>
        {banners.length > 1 && (
          <>
            <button onClick={prev} data-testid="banner-prev" aria-label="Previous"
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/45 hover:bg-black/70 flex items-center justify-center text-white">
              <ChevronLeft size={15} />
            </button>
            <button onClick={next} data-testid="banner-next" aria-label="Next"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/45 hover:bg-black/70 flex items-center justify-center text-white">
              <ChevronRight size={15} />
            </button>
            <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="banner-dots">
              {banners.map((x, i) => (
                <button key={x.id} onClick={() => setIdx(i)} aria-label={`Banner ${i + 1}`}
                        className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-1.5 bg-white/40"}`} />
              ))}
            </div>
          </>
        )}
      </div>
      <style>{`@keyframes bannerIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } } @keyframes bannerKenBurns { from { transform: scale(1); } to { transform: scale(1.08); } } .icaard-banner-image { animation: bannerKenBurns 12s ease-in-out alternate infinite; transform-origin: center; } @media (prefers-reduced-motion: reduce) { .icaard-banner-image { animation: none; } }`}</style>
    </section>
  );
};

export default BannerCarousel;
